import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import Input from 'components/final-form/Input';
import Radio from 'components/final-form/Radio';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Header, RadioProps } from 'semantic-ui-react';
import { getTreatmentsForAutomaticBilling } from 'services/billServices';
import axios from 'services/http';
import styled from 'styled-components';
import { TreatmentsOrderBy, TreatmentStatus } from 'ts-types/api.enums';
import {
  AutomaticBillingSearchRequest,
  AutomaticBillingSearchResponseWrapper,
  AutomaticBillRunSearchData,
  TariffDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 1em 1em;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

   flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
      font-size: 1em !important;
    }
`;

const TableContainer = styled.div`
  flex: 1 1 auto;
  min-height: 172px;
  max-width: 300px;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34,36,38,.15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34,36,38,.15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34,36,38,.15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 80px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 1 1 33%;
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

interface State {
  searchData: Partial<AutomaticBillingSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onFilterTreatments: (searchResponseWrapper: AutomaticBillingSearchResponseWrapper) => void,
  onTreatmentsLoadingChange: (treatmentsLoading: boolean) => void,
  tariffs: Array<TariffDto>,
  setAutomaticBillRunSearchData: (searchData: AutomaticBillRunSearchData) => void,
  setErrorMessage: (errorMessage?: string) => void
}

class AutomaticBillingOptionsSegment extends Component<Props, State> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {getMessage} = props;

    const searchByOptionLabels = Object.values(TreatmentStatus);
    const orderByValues = Object.values(TreatmentsOrderBy);

    const searchByOptions =
        generateRadioButtonOptionsFromEnum(searchByOptionLabels, "treatmentStatus", getMessage);

    const orderBy = generateRadioButtonOptionsFromEnum(orderByValues, "orderBy", getMessage);

    searchByOptions.forEach((radioDefinition, index) => {
      radioMap.set(searchByOptionLabels[index], radioDefinition);
    });

    orderBy.forEach((radioDefinition, index) => {
      radioMap.set(orderByValues[index], radioDefinition);
    });

    this.state = {
      searchData: this.initialValues()
    }
  };

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const {searchData} = this.state;
    if (prevProps.tariffs !== this.props.tariffs) {

      const tariffIds = this.props.tariffs.map(tariff => tariff.id);

      this.setState({
        searchData: {...searchData, selectedTariffIds: tariffIds}
      })
    }
  }

  initialValues = (): Partial<AutomaticBillingSearchRequest> => {

    const {tariffs, options: {workDate}, currentUser} = this.props;

    const tariffIds = tariffs.map(tariff => tariff.id);

    return {
      fromDate: workDate,
      toDate: workDate,
      treatmentStatus: TreatmentStatus.OPEN,
      consultationPeriod: true,
      includeInterrupted: false,
      selectedTariffIds: tariffIds,
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      orderBy: TreatmentsOrderBy.TREATMENT_DATE
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {

    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }
  };

  searchTreatments = (values: Partial<AutomaticBillingSearchRequest>) => {

    const {
      onTreatmentsLoadingChange,
      onFilterTreatments,
      tariffs,
      setErrorMessage,
      setAutomaticBillRunSearchData,
      getMessage
    } = this.props;

    this.setState({
      searchData: values
    });

    onTreatmentsLoadingChange(true);

    const selectedTariffsArray: Array<string> = [];
    tariffs.forEach(tariff => {
      const selectedTariffIds = values.selectedTariffIds;
      if (selectedTariffIds && selectedTariffIds.length > 0) {
        if (selectedTariffIds.includes(tariff.id || tariff.lnkTrfId)) {
          selectedTariffsArray.push(tariff.description);
        }
      }
    });

    const selectedTariffs = selectedTariffsArray.join(", ");

    const searchData: AutomaticBillRunSearchData = {
      fromDate: values.fromDate,
      toDate: values.toDate,
      selectedTariffs: selectedTariffs
    }
    setAutomaticBillRunSearchData(searchData);

    getTreatmentsForAutomaticBilling(values, this.cancelTokenSource)
        .then(response => {
          onFilterTreatments(response);
        })
        .catch(() => {
          setErrorMessage(getMessage("error.general"));
        })
        .finally(() => {
          onTreatmentsLoadingChange(false);
        })
  };

  selectTariff = (values: AutomaticBillingSearchRequest, form: FormApi, id: number) => {
    const tariffSelected = values.selectedTariffIds.includes(id);
    if (tariffSelected) {
      const tariffs = values.selectedTariffIds.filter((tariffId: number) => tariffId !== id);
      form.change("selectedTariffIds", tariffs);
    } else {
      const tariffs = [...values.selectedTariffIds, id];
      form.change("selectedTariffIds", tariffs);
    }
  }

  onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.searchTreatments(values)
    }
  };

  render(): React.ReactElement {

    const {searchData} = this.state;

    return (
        <FinalForm
            onSubmit={() => noop}
            initialValues={searchData}
            subscription={{values: true, pristine: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props} = this;
    const {tariffs, getMessage} = props;

    const treatmentOpen = radioMap.get(TreatmentStatus.OPEN);
    const treatmentClosed = radioMap.get(TreatmentStatus.CLOSED);

    const treatmentDate = radioMap.get(TreatmentsOrderBy.TREATMENT_DATE);
    const patientName = radioMap.get(TreatmentsOrderBy.PATIENT_NAME);
    const guarantorName = radioMap.get(TreatmentsOrderBy.GUARANTOR_NAME);

    const disabledBefore = values.fromDate && new Date(values.fromDate);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <Header>{getMessage("searchOptions")}</Header>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => this.onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <SearchGroup>
                <Header size="tiny">{getMessage("automaticBilling.termination")}</Header>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("patient.search.checkbox.externalIdManual")}:
                  </StyledLabel>
                  <Field
                      name="externalPatientId"
                      component={Input}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="treatmentStatus"
                      component={Radio}
                      radioDefinition={treatmentOpen}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="treatmentStatus"
                      component={Radio}
                      radioDefinition={treatmentClosed}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="includeInterrupted"
                      component={CheckBox}
                      label={getMessage('automaticBilling.includeInterrupted.checkbox')}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="consultationPeriod"
                      component={CheckBox}
                      label={getMessage('automaticBilling.charging')}
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("automaticBilling.tariffSelection")}</Header>
                <TableContainer>
                  {this.renderTariffsTable(form, values as AutomaticBillingSearchRequest, tariffs)}
                </TableContainer>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("automaticBilling.orderBy")}</Header>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={treatmentDate}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={patientName}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={guarantorName}
                  />
                </SearchField>
              </SearchGroup>
              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => this.searchTreatments(values)}>
                  {getMessage("search")}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  };

  selectHeaderColumn = (form: FormApi, values: AutomaticBillingSearchRequest, tariffs: Array<TariffDto>) => {

    const countSelectedTariffs = values.selectedTariffIds.length;
    const indeterminate = countSelectedTariffs > 0 && countSelectedTariffs < tariffs.length;
    const noSelectedTariffs = countSelectedTariffs === 0 && tariffs.length > 0;
    const allTariffsSelected = tariffs.length > 0 && countSelectedTariffs === tariffs.length;

    return (
        <CheckboxSc
            checked={countSelectedTariffs > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedTariffs || indeterminate) {
                  const selectedTariffs = tariffs.map(tariff => tariff.id);
                  form.change("selectedTariffIds", selectedTariffs);
                }

                if (allTariffsSelected) {
                  form.change("selectedTariffIds", []);
                }

              }
            }
            disabled={tariffs.length === 0}
        />
    )
  };

  tariffsRowGetter = (tariffs: Array<TariffDto>) => ({index}: any) => {

    if (tariffs[index]) {
      Object.assign(tariffs[index], {index: index + 1});

      return tariffs[index];
    }

    return {
      index: index + 1
    }
  };

  tariffsRowRenderer = (form: FormApi, values: AutomaticBillingSearchRequest) => ({className, columns, index, key, style, rowData}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.selectTariff(values, form, rowData.id)}
        >
          {columns}
        </div>
    );
  };

  tariffSelectCellRenderer = (data: any, values: AutomaticBillingSearchRequest, form: FormApi) => {

    const {rowData} = data;
    const {id} = rowData;

    const tariffSelected = values.selectedTariffIds.includes(id);

    return (
        <CheckboxSc
            checked={tariffSelected}
            onChange={() => this.selectTariff(values, form, id)}
        />
    )
  };

  tariffDescriptionCellRenderer = (data: any) => {

    return <div>{data.rowData.description}</div>
  };

  renderTariffsTable = (form: FormApi, values: AutomaticBillingSearchRequest, tariffs: Array<TariffDto>): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.tariffsRowGetter(tariffs)}
            rowRenderer={this.tariffsRowRenderer(form, values)}
            rowCount={tariffs.length}
            columns={[
              {
                width: 30,
                label: this.selectHeaderColumn(form, values, tariffs),
                dataKey: "id",
                cellRenderer: (data: any) => this.tariffSelectCellRenderer(data, values, form)
              },
              {
                width: 180,
                label: getMessage("description"),
                dataKey: "description",
                cellRenderer: this.tariffDescriptionCellRenderer
              }
            ]}
        />
    )
  };

}

export default withAuthContext(withMessages(AutomaticBillingOptionsSegment));
